import { Spin } from "antd";
import i18n from "i18next";
import type { FC } from "react";
import { useCallback, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import type { EmvConfigurationQueryParams } from "src/app/features/EmvKernelProfile/subFeatures/EmvConfiguration/services/server/EmvConfigurationQueryTypes";
import { FormManager } from "../../../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../../../components/Form/types";
import type { FormProps } from "../../../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../../../core/setup/redux/state/notifications/notificationsState";
import { useEmvConfigurationUpdateMutation, useLazyEmvConfigurationLoadQuery } from "../../services/server/EmvConfigurationApi";
import type { EmvConfigurationEntity } from "../../types/EmvConfigurationEntity";

export const EmvConfigurationEditForm: FC<FormProps> = ({ runSubmit }) => {
    const navigate = useNavigate();
    const { emvKernelProfileId, id } = useParams();
    const [queryConfig, setQueryConfig] = useState<EmvConfigurationQueryParams>({});

    // set save params
    useEffect(() => {
        setQueryConfig({ emvKernelProfileId, id });
    }, [emvKernelProfileId, id]);

    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("emv-configuration");
    const [loadFormData, { isFetching: loadFormIsInProgress }] = useLazyEmvConfigurationLoadQuery();

    const [formData, setFormData] = useState<EmvConfigurationEntity>({} as EmvConfigurationEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    // load form data
    useEffect(() => {
        if (id && queryConfig.emvKernelProfileId) {
            loadFormData(queryConfig).then((loadedData) => {
                if (loadedData.data) {
                    setFormData(loadedData.data);
                }
            });
        }
    }, [id, loadFormData, queryConfig]);

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useEmvConfigurationUpdateMutation();
    const dispatch = useDispatch();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }

            const result = await saveFormData({ emvConfigurationFormData: formData, emvConfigurationQuery: queryConfig });

            const hasError = "error" in result;
            if (!hasError) {
                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formUpdateSuccess", {
                            id: result.data.id,
                            resource: "EmvConfiguration",
                        }),
                        autoCloseDisabled: false,
                    }),
                );
                navigate(
                    `${BASE_PATH}/${ResourceName.EMV_KERNEL_PROFILES}/${ResourceVariant.APAC}/${FORM_ACTION.EDIT}/${emvKernelProfileId}`,
                );
            }
        },
        [formData, saveFormData, queryConfig, dispatch, navigate, emvKernelProfileId],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching || loadFormIsInProgress}>
            {formDefinition?.formConfiguration && (
                <FormManager<EmvConfigurationEntity>
                    name="EmvConfigurationEditForm"
                    formData={formData}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
